import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "../../components/Layout";
import { api } from "../../lib/api";
import { ArrowLeft, Download, Eraser, Filter, ArrowUpDown } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Write-off / breakage review — month-end inspection surface.
 * Filterable by site + item + date range; sortable; CSV export.
 */
export default function WriteOffReview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = user?.role === "admin" || !!user?.permissions_effective?.view_inventory;
  const [sites, setSites] = useState([]);
  const [items, setItems] = useState([]);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [site, setSite] = useState("");
  const [itemId, setItemId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("at");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data?.sites || [])).catch(() => {});
    api.get("/inventory/items", { params: { active: true } }).then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/write-offs", {
        params: { from, to, site: site || undefined, item_id: itemId || undefined },
      });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [from, to, site, itemId]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/inventory/write-offs.csv", {
        params: { from, to, site: site || undefined, item_id: itemId || undefined },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `write_offs_${from}_${to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const sortedRows = React.useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => {
      const av = a[sortBy] ?? "";
      const bv = b[sortBy] ?? "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  if (!canView) {
    return <AppShell title="Write-offs"><div className="p-10 text-center text-slate-500">No inventory access</div></AppShell>;
  }

  return (
    <AppShell title="Inventory Reports">
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-5" data-testid="writeoff-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            data-testid="wo-back"
            onClick={() => navigate("/inventory")}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft size={13} strokeWidth={2.2} /> Inventory home
          </button>
          <button
            data-testid="wo-csv"
            onClick={downloadCsv}
            className="rounded-full px-3.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 bg-olive text-white hover:opacity-90"
          >
            <Download size={13} strokeWidth={2} /> CSV
          </button>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 lg:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-red-50 grid place-items-center text-red-600 shrink-0">
              <Eraser size={20} strokeWidth={1.8} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">CCG / Inventory</div>
              <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight mt-0.5">Write-off &amp; Breakage Review</h1>
              <p className="text-sm text-slate-500 mt-2">
                Every write-off in the selected period — valued at the rate in effect on its date. Use this for month-end review and to spot unusual breakage.
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3 items-end" data-testid="wo-filters">
            <Field label="From">
              <input data-testid="wo-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls}/>
            </Field>
            <Field label="To">
              <input data-testid="wo-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Site">
              <select data-testid="wo-site" value={site} onChange={(e) => setSite(e.target.value)} className={inputCls}>
                <option value="">All sites</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Item">
              <select data-testid="wo-item-filter" value={itemId} onChange={(e) => setItemId(e.target.value)} className={inputCls}>
                <option value="">All items</option>
                {items.map((i) => <option key={i.item_id} value={i.item_id}>{i.name}</option>)}
              </select>
            </Field>
            <button
              data-testid="wo-apply"
              onClick={load}
              className="rounded-xl px-4 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 bg-slate-900 text-white hover:bg-olive transition-colors h-[38px]"
            >
              <Filter size={12} strokeWidth={2.2} /> Apply
            </button>
          </div>
        </section>

        {/* Totals */}
        {data && (
          <section className="grid grid-cols-2 gap-3" data-testid="wo-totals">
            <StatTile testid="wo-total-qty" tone="slate" label="Total quantity" value={data.total_quantity ?? 0} />
            <StatTile testid="wo-total-value" tone="red" label="Total value" value={fmtINR(data.total_value ?? 0)} />
          </section>
        )}

        <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : sortedRows.length === 0 ? (
            <div className="p-10 text-center" data-testid="wo-empty">
              <Eraser className="mx-auto text-slate-300" size={28} strokeWidth={1.5} />
              <div className="text-sm font-medium text-slate-900 mt-2">No write-offs in this period</div>
              <p className="text-xs text-slate-500 mt-1">Adjust the filters or record a write-off from the inventory home toolbar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="wo-table-wrap">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/60">
                    <Th label="Date" col="at" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}/>
                    <Th label="Site" col="site" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}/>
                    <Th label="Item" col="item_name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort}/>
                    <th className="text-right px-3 py-2.5">Qty</th>
                    <th className="text-right px-3 py-2.5">Rate</th>
                    <Th label="Value" col="value" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} align="right"/>
                    <th className="text-left px-3 py-2.5 hidden lg:table-cell">Reason</th>
                    <th className="text-left px-3 py-2.5 hidden md:table-cell">Logged by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-testid="wo-rows">
                  {sortedRows.map((r) => (
                    <tr key={r.movement_id} data-testid={`wo-row-${r.movement_id}`}>
                      <td className="px-3 py-2.5 text-slate-500 text-[11px] whitespace-nowrap">
                        {new Date(r.at).toLocaleDateString()}{" "}
                        <span className="text-slate-400">{new Date(r.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{r.site}</td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-900">{r.item_name}</div>
                        <div className="text-[10px] text-slate-400">{r.category} · {r.unit}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.quantity}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{r.rate != null ? fmtINR(r.rate) : "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-red-700">{r.value != null ? fmtINR(r.value) : <span className="text-slate-400 italic font-normal">unrated</span>}</td>
                      <td className="px-3 py-2.5 text-slate-600 max-w-xs truncate hidden lg:table-cell" title={r.reason}>{r.reason || "—"}</td>
                      <td className="px-3 py-2.5 text-slate-500 hidden md:table-cell">{r.by_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-[10px] text-slate-400 text-center print:hidden">
          Values use the rate in effect on each write-off date. Unrated entries reflect items with no rate history yet.
        </p>
      </div>
    </AppShell>
  );
}

const Th = ({ label, col, sortBy, sortDir, onClick, align = "left" }) => (
  <th className={(align === "right" ? "text-right" : "text-left") + " px-3 py-2.5 cursor-pointer select-none"} onClick={() => onClick(col)}>
    <span className="inline-flex items-center gap-1">
      {label}
      {sortBy === col && <ArrowUpDown size={10} strokeWidth={2.2} className={"text-olive " + (sortDir === "asc" ? "rotate-180" : "")} />}
    </span>
  </th>
);

const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30";
const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
const StatTile = ({ testid, label, value, tone = "slate" }) => {
  const t = {
    red: "bg-red-50 text-red-700 border-red-200",
    slate: "bg-white text-slate-700 border-slate-200",
  }[tone] || "bg-white text-slate-700 border-slate-200";
  return (
    <div data-testid={testid} className={"rounded-2xl border px-4 py-3.5 " + t}>
      <div className="text-[11px] font-medium uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-semibold tracking-tight mt-1 tabular-nums">{value}</div>
    </div>
  );
};

function fmtINR(n) {
  if (n == null) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
